import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Select, Space, message, Card, InputNumber, TreeSelect } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import questionService from '../services/question';
import knowledgeService from '../services/knowledgeService';
import tagService from '../services/tag';

const { Option } = Select;
const { TextArea } = Input;

const QuestionEdit = () => {
  const [form] = Form.useForm();
  const [questionTypes, setQuestionTypes] = useState([]);
  const [knowledgeTree, setKnowledgeTree] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    fetchQuestionTypes();
    fetchKnowledgeTree();
    fetchTags();
    if (id) {
      fetchQuestion();
    }
  }, [id]);

  const fetchQuestionTypes = async () => {
    try {
      const response = await questionService.getQuestionTypes();
      console.log('获取题目类型成功:', response);
      if (response && response.data && Array.isArray(response.data.items)) {
        console.log('使用response.data.items作为数据');
        setQuestionTypes(response.data.items);
      } else if (response && Array.isArray(response.data)) {
        console.log('使用response.data作为数据');
        setQuestionTypes(response.data);
      } else if (Array.isArray(response)) {
        console.log('直接使用response作为数据');
        setQuestionTypes(response);
      } else {
        console.log('数据格式不正确，设置为空数组');
        setQuestionTypes([]);
      }
    } catch (error) {
      console.error('获取题目类型失败:', error);
      message.error('获取题目类型失败');
      setQuestionTypes([]);
    }
  };

  const fetchKnowledgeTree = async () => {
    try {
      const response = await knowledgeService.getKnowledgeTree();
      console.log('获取知识体系树成功:', response);
      if (response && response.data) {
        setKnowledgeTree(response.data);
      } else {
        setKnowledgeTree([]);
      }
    } catch (error) {
      console.error('获取知识体系树失败:', error);
      message.error('获取知识体系树失败');
      setKnowledgeTree([]);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await tagService.getTags();
      console.log('获取标签列表成功:', response);
      if (response && response.data && Array.isArray(response.data.items)) {
        console.log('使用response.data.items作为数据');
        setTags(response.data.items);
      } else if (response && Array.isArray(response.data)) {
        console.log('使用response.data作为数据');
        setTags(response.data);
      } else if (Array.isArray(response)) {
        console.log('直接使用response作为数据');
        setTags(response);
      } else {
        console.log('数据格式不正确，设置为空数组');
        setTags([]);
      }
    } catch (error) {
      console.error('获取标签列表失败:', error);
      message.error('获取标签列表失败');
      setTags([]);
    }
  };

  const fetchQuestion = async () => {
    try {
      const response = await questionService.getQuestion(id);
      const question = response.data;
      form.setFieldsValue({
        type_id: question.type_id,
        content: question.content,
        difficulty: question.difficulty,
        status: question.status,
        knowledge_ids: question.knowledge_ids || [],
        tag_names: question.tags.map(t => t.tag_name),
        options: question.options,
        answers: question.answers,
        analysis: question.analysis?.analysis_content,
      });
    } catch (error) {
      message.error('获取题目详情失败');
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      if (id) {
        await questionService.updateQuestion(id, values);
        message.success('更新成功');
      } else {
        await questionService.createQuestion(values);
        message.success('创建成功');
      }
      navigate('/questions');
    } catch (error) {
      message.error(id ? '更新失败' : '创建失败');
    }
    setLoading(false);
  };

  // 将知识体系数据转换为TreeSelect需要的格式
  const transformKnowledgeData = (data) => {
    return data.map(item => ({
      title: item.name,
      value: item.id,
      key: item.id,
      disabled: item.level < 5, // 允许选择章节（level=5）和知识点（level=6）
      children: item.children ? transformKnowledgeData(item.children) : undefined
    }));
  };

  return (
    <Card title={id ? '编辑题目' : '创建题目'}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          type_id: undefined,
          content: '',
          difficulty: 1,
          options: [],
          answers: [],
          analysis: '',
          knowledge_ids: [],
          tag_names: []
        }}
      >
        <Form.Item
          name="type_id"
          label="题目类型"
          rules={[{ required: true, message: '请选择题目类型' }]}
        >
          <Select>
            {(questionTypes || []).map(type => (
              <Option key={type.id} value={type.id}>{type.type_name}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="content"
          label="题目内容"
          rules={[{ required: true, message: '请输入题目内容' }]}
        >
          <TextArea rows={4} />
        </Form.Item>

        <Form.Item
          name="difficulty"
          label="难度"
          rules={[{ required: true, message: '请选择难度' }]}
        >
          <Select>
            <Option value={1}>简单</Option>
            <Option value={2}>较易</Option>
            <Option value={3}>中等</Option>
            <Option value={4}>较难</Option>
            <Option value={5}>困难</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="status"
          label="状态"
          rules={[{ required: true, message: '请选择状态' }]}
        >
          <Select>
            <Option value={1}>启用</Option>
            <Option value={0}>禁用</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="knowledge_ids"
          label="知识点"
          rules={[{ required: true, message: '请选择章节或知识点' }]}
          extra="可以选择章节或具体知识点"
        >
          <TreeSelect
            treeData={transformKnowledgeData(knowledgeTree)}
            treeCheckable
            showCheckedStrategy={TreeSelect.SHOW_PARENT}
            placeholder="请选择章节或知识点"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item
          name="tag_names"
          label="标签"
        >
          <Select mode="tags" tokenSeparators={[',']}>
            {(tags || []).map(tag => (
              <Option key={tag.id} value={tag.tag_name}>{tag.tag_name}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.List name="options">
          {(fields, { add, remove }) => (
            <div>
              {fields.map(({ key, name, ...restField }) => (
                <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                  <Form.Item
                    {...restField}
                    name={[name, 'option_content']}
                    rules={[{ required: true, message: '请输入选项内容' }]}
                  >
                    <Input placeholder="选项内容" />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'is_correct']}
                    valuePropName="checked"
                  >
                    <Select style={{ width: 100 }}>
                      <Option value={true}>正确</Option>
                      <Option value={false}>错误</Option>
                    </Select>
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'option_order']}
                  >
                    <InputNumber min={0} placeholder="顺序" />
                  </Form.Item>
                  <Button type="link" onClick={() => remove(name)}>
                    删除
                  </Button>
                </Space>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block>
                  添加选项
                </Button>
              </Form.Item>
            </div>
          )}
        </Form.List>

        <Form.List name="answers">
          {(fields, { add, remove }) => (
            <div>
              {fields.map(({ key, name, ...restField }) => (
                <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                  <Form.Item
                    {...restField}
                    name={[name, 'answer_content']}
                    rules={[{ required: true, message: '请输入答案内容' }]}
                  >
                    <Input placeholder="答案内容" />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'answer_type']}
                  >
                    <Select style={{ width: 100 }}>
                      <Option value={1}>文本</Option>
                      <Option value={2}>选项ID</Option>
                    </Select>
                  </Form.Item>
                  <Button type="link" onClick={() => remove(name)}>
                    删除
                  </Button>
                </Space>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block>
                  添加答案
                </Button>
              </Form.Item>
            </div>
          )}
        </Form.List>

        <Form.Item
          name="analysis"
          label="解析"
        >
          <TextArea rows={4} />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>
              {id ? '更新' : '创建'}
            </Button>
            <Button onClick={() => navigate('/questions')}>
              取消
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default QuestionEdit; 